import { useState, useEffect } from 'react';
export type { ProposalStatus } from '../../../../shared/proposal-contract.js';
import type { ProposalStatus } from '../../../../shared/proposal-contract.js';
import { Avatar, Spinner } from '../ui';
import { formatDate, formatTime } from '../../lib/date-utils';
import { TraceDrawer, type GenerationContext, type ObserverSignals } from './TraceDrawer';

export type { MatchCategory, SkipReason } from '../../../../shared/proposal-contract.js';
import type { MatchCategory, SkipReason } from '../../../../shared/proposal-contract.js';

/**
 * Почему черновика нет — словами, которые видит оператор.
 *
 * `Record<SkipReason, string>` осознанно: пропущенное значение не
 * скомпилируется. До 09.09 тип был объявлен здесь заново и разошёлся с
 * контрактом — в нём не было ни одной причины, которую бэкенд реально пишет
 * в базу, поэтому оператор видел «skipped: » без причины.
 *
 * По-русски, как весь интерфейс. Прежние подписи были английскими.
 */
const SKIP_REASON_LABELS: Record<SkipReason, string> = {
  hard_short: 'слишком короткое сообщение',
  soft_short: 'короткое сообщение',
  system: 'системное сообщение',
  already_processed: 'уже обработано',
  not_client: 'сообщение не от клиента',
  strategy_silence: 'движок решил промолчать',
  superseded: 'заменён новым черновиком',
  no_conversation: 'беседы ещё не было — нажмите «Подготовить ответ»',
  manual_only: 'черновики только по кнопке',
  guardrails: 'похоже на рассылку или автоответ — отвечать некому',
  explicit_no: 'клиент попросил не продолжать',
  supervisor_block: 'супервизор не пропустил ответ',
};

export interface Proposal {
  id: string;
  text: string;
  /**
   * Что предложил движок до правки оператора. `null` = не правили.
   *
   * Показывается рядом с итоговым текстом: без этого правка выглядела как
   * работа движка, и по интерфейсу было не отличить «ответил сам» от
   * «переписали от начала до конца». Обучение сравнивает те же два текста
   */
  originalText?: string | null;
  status: ProposalStatus;
  matchScore: number | null;
  matchCategory: MatchCategory | null;
  generationContext: GenerationContext | null;
  observerSignals: ObserverSignals | null;
  shadowDecision: string | null;
  skipReason: SkipReason | null;
  createdAt: string;
  // Human review flags
  askHuman?: boolean;
  needsHumanQuality?: boolean;
}

/**
 * Отказ принятия, привязанный к конкретному черновику.
 *
 * `kind` различает две вещи, которые нельзя показывать одинаково:
 * `failed` — ответ клиенту НЕ ушёл, `already` — черновик уже обработан
 * (обычно вторым щелчком), то есть ответ как раз ушёл. Написать
 * «Не отправлено» во втором случае значит соврать оператору.
 */
export interface ProposalActionError {
  proposalId: string;
  message: string;
  kind: 'failed' | 'already';
}

export interface ProposalReviewActions {
  onAccept: (proposalId: string) => void;
  onReject: (proposalId: string) => void;
  /**
   * Отправить правку. Возвращает признак успеха.
   *
   * Именно признак, а не `void`: редактор нельзя закрывать до
   * подтверждения. Маршрут отвечает 409, когда черновик успели принять или
   * отклонить, пока текст правили, — а закрытый редактор с прежним текстом
   * выглядит ровно как удачное сохранение (найдено ревью 08.09).
   */
  onEdit: (proposalId: string, text: string) => Promise<boolean> | void;
  /** Почему принятие не удалось. Молчаливый отказ равен неработающей кнопке. */
  acceptError?: ProposalActionError | null;
  /** Запрос в полёте: вторая отправка того же черновика не нужна. */
  isAccepting?: boolean;
}

interface ProposalBubbleProps {
  proposal: Proposal;
  reviewActions?: ProposalReviewActions | undefined;
}

function getStatusBadge(status: ProposalStatus): { color: string; label: string } {
  switch (status) {
    case 'thinking':
      return { color: 'bg-accent-warning/10 text-accent-warning border border-accent-warning/20', label: 'генерация' };
    case 'ready':
      return { color: 'bg-accent-success/10 text-accent-success border border-accent-success/20', label: 'ready' };
    case 'skipped':
      return { color: 'bg-surface-subtle text-text-secondary border border-border-subtle', label: 'skip' };
    case 'matched':
      return { color: 'bg-accent-brand/10 text-accent-brand border border-accent-brand/20', label: 'matched' };
    default:
      return { color: 'bg-surface-subtle text-text-secondary border border-border-subtle', label: status };
  }
}

export function ProposalBubble({ proposal, reviewActions }: ProposalBubbleProps): JSX.Element {
  const [isTraceOpen, setIsTraceOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(proposal.text);
  const { color, label } = getStatusBadge(proposal.status);

  const isReviewable = proposal.status === 'ready' &&
    (proposal.shadowDecision === 'allow' || proposal.shadowDecision === 'soften') &&
    !!reviewActions;

  // Слушаем keyboard shortcuts A и E из InboxPage.
  //
  // Принятие идёт тем же путём, что и кнопка, а не своим вызовом мутации:
  // страница вызывала её отдельно, и отказ отправки по клавише пропадал
  // молча даже после того, как кнопка научилась его показывать
  useEffect(() => {
    if (!isReviewable) return;
    function handleEditEvent(e: Event) {
      const detail = (e as CustomEvent<{ proposalId: string }>).detail;
      if (detail.proposalId === proposal.id) {
        setIsEditing(true);
        setEditText(proposal.text);
      }
    }
    function handleAcceptEvent(e: Event) {
      const detail = (e as CustomEvent<{ proposalId: string }>).detail;
      if (detail.proposalId === proposal.id) {
        reviewActions?.onAccept(proposal.id);
      }
    }
    window.addEventListener('proposal-edit-start', handleEditEvent);
    window.addEventListener('proposal-accept', handleAcceptEvent);
    return () => {
      window.removeEventListener('proposal-edit-start', handleEditEvent);
      window.removeEventListener('proposal-accept', handleAcceptEvent);
    };
  }, [isReviewable, proposal.id, proposal.text, reviewActions]);

  return (
    <>
      <div className="flex gap-3 max-w-[90%] ml-auto flex-row-reverse">
        <div className="opacity-60">
          <Avatar name="Shadow" size="sm" />
        </div>

        <div className="text-right">
          <div className="flex items-center gap-2 mb-1 justify-end">
            {/* Trace button */}
            <button
              onClick={() => setIsTraceOpen(true)}
              className="p-1 hover:bg-surface-subtle rounded transition-colors text-text-secondary hover:text-accent-ai"
              title="View trace"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
            <span className="text-xs text-text-secondary">
              {formatDate(proposal.createdAt)}, {formatTime(proposal.createdAt)}
            </span>
            <span className={`px-2 py-0.5 text-xs rounded-full ${color}`}>
              {label}
            </span>
            <span className="text-sm font-medium text-text-primary">
              Shadow
            </span>
          </div>

          {/* Human review warning */}
          {(proposal.askHuman || proposal.needsHumanQuality) && proposal.status !== 'skipped' && (
            <div className={`mb-2 px-3 py-1.5 rounded-comp text-xs flex items-center gap-2 ${proposal.askHuman
                ? 'bg-accent-danger/10 border border-accent-danger/20 text-accent-danger'
                : 'bg-accent-warning/10 border border-accent-warning/20 text-accent-warning'
              }`}>
              <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span>
                {proposal.askHuman
                  ? 'Требуется человек (P0 риск)'
                  : 'Проверь перед отправкой'}
              </span>
            </div>
          )}

          <div className="rounded-2xl px-4 py-3 bg-accent-ai/10 rounded-tr-md break-words border border-accent-ai/20 shadow-sm">
            {proposal.status === 'thinking' ? (
              <div className="flex items-center gap-2">
                <Spinner size="sm" className="text-accent-ai" />
                <span className="text-sm text-text-secondary">генерация...</span>
              </div>
            ) : proposal.status === 'skipped' ? (
              <p className="text-sm text-text-secondary italic">
                Черновика нет: {proposal.skipReason ? SKIP_REASON_LABELS[proposal.skipReason] : 'причина не записана'}
              </p>
            ) : isEditing ? (
              <div className="space-y-2">
                <textarea
                  className="w-full min-h-[80px] p-2 text-sm text-text-primary bg-white border border-border-subtle rounded-comp resize-y focus:outline-none focus:border-accent-brand"
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  autoFocus
                />
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => { setIsEditing(false); setEditText(proposal.text); }}
                    className="px-3 py-1 text-xs text-text-secondary hover:text-text-primary rounded-comp border border-border-subtle hover:border-border-strong transition-colors"
                  >
                    Отмена
                  </button>
                  <button
                    onClick={async () => {
                      // Редактор закрывается ТОЛЬКО после подтверждения:
                      // иначе отказ выглядит как сохранение, а текст правки
                      // теряется
                      const ok = await reviewActions?.onEdit(proposal.id, editText);
                      if (ok !== false) {
                        setIsEditing(false);
                      }
                    }}
                    disabled={!editText.trim() || editText === proposal.text}
                    className="px-3 py-1 text-xs text-white bg-accent-brand rounded-comp hover:bg-accent-brand/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Отправить
                  </button>
                </div>
              </div>
            ) : proposal.text ? (
              <div className="space-y-2">
                <p className="text-sm text-text-primary whitespace-pre-wrap">
                  {proposal.text}
                </p>
                {proposal.originalText && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-text-secondary hover:text-text-primary">
                      Правлено — что предлагал движок
                    </summary>
                    <p className="mt-1.5 pl-2 border-l-2 border-border-subtle text-text-secondary whitespace-pre-wrap">
                      {proposal.originalText}
                    </p>
                  </details>
                )}
              </div>
            ) : (
              <p className="text-sm text-text-secondary italic">(пусто)</p>
            )}
          </div>

          {/* Отказ принятия. «Не отправлено» — только когда ответ ТОЧНО не ушёл */}
          {reviewActions?.acceptError?.proposalId === proposal.id && (
            <p
              role="alert"
              className={
                reviewActions.acceptError.kind === 'already'
                  ? 'mt-2 px-2 py-1.5 text-xs text-text-secondary bg-surface-subtle border border-border-subtle rounded-comp'
                  : 'mt-2 px-2 py-1.5 text-xs text-accent-danger bg-accent-danger/10 border border-accent-danger/20 rounded-comp'
              }
            >
              {reviewActions.acceptError.kind === 'already'
                ? reviewActions.acceptError.message
                : `Не отправлено: ${reviewActions.acceptError.message}`}
            </p>
          )}

          {/* Review actions */}
          {isReviewable && !isEditing && (
            <div className="flex gap-2 mt-2 justify-end">
              <button
                onClick={() => reviewActions.onAccept(proposal.id)}
                disabled={reviewActions.isAccepting === true}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-accent-brand bg-accent-brand/10 border border-accent-brand/20 rounded-comp hover:bg-accent-brand/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Принять (A)"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                Принять
              </button>
              <button
                onClick={() => { setIsEditing(true); setEditText(proposal.text); }}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-text-primary bg-surface-subtle border border-border-subtle rounded-comp hover:bg-surface-elevated transition-colors"
                title="Редактировать (E)"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Изменить
              </button>
              <button
                onClick={() => reviewActions.onReject(proposal.id)}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-accent-danger bg-accent-danger/10 border border-accent-danger/20 rounded-comp hover:bg-accent-danger/20 transition-colors"
                title="Отклонить (R)"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                Отклонить
              </button>
            </div>
          )}

          {(proposal.matchCategory || proposal.matchScore !== null) && (
            <div className="flex items-center gap-2 mt-1 justify-end text-xs text-text-secondary">
              {proposal.matchScore !== null && (
                <span className="tabular-nums">{Math.round(proposal.matchScore * 100)}%</span>
              )}
              {proposal.matchCategory && (
                <span className={`px-1.5 py-0.5 rounded ${proposal.matchCategory === 'used'
                    ? 'bg-accent-success/10 text-accent-success border border-accent-success/20'
                    : proposal.matchCategory === 'partial'
                      ? 'bg-accent-warning/10 text-accent-warning border border-accent-warning/20'
                      : 'bg-surface-subtle text-text-secondary border border-border-subtle'
                  }`}>
                  {proposal.matchCategory === 'used' ? 'Совпало' :
                    proposal.matchCategory === 'partial' ? 'Частично' : 'Не совпало'}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Trace Drawer */}
      <TraceDrawer
        isOpen={isTraceOpen}
        onClose={() => setIsTraceOpen(false)}
        proposalId={proposal.id}
        generationContext={proposal.generationContext}
        observerSignals={proposal.observerSignals}
        shadowDecision={proposal.shadowDecision}
        createdAt={proposal.createdAt}
      />
    </>
  );
}
